import { Plus } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useMatch } from 'react-router';
import { ListToolbar } from '@/components/list/ListToolbar';
import { useListParams } from '@/components/list/use-list-params';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAccessActions } from '../people/person-actions';
import { usePlaceContext, type PlaceContext } from '../place/PlaceLayout';
import { apiPlace, sectionPath } from '../place/place';
import { PageHead } from '../ui/PageHead';
import { PhonePrimary } from '../ui/PhonePrimary';
import { EditInvitationRolesPanel } from './EditInvitationRolesPanel';
import { InvitePanel } from './InvitePanel';
import { InvitationListPane } from './InvitationListPane';
import type { InvitationClosing } from './InvitationSheet';
import {
  useInvitations,
  type Invitation,
  type InvitationFilters,
  type InvitationStatus,
} from './use-invitations';

const STATUSES: readonly InvitationStatus[] = ['pending', 'accepted', 'expired', 'withdrawn'];
const FILTER_KEYS = ['status'] as const;

/** What the list hands its card (`InvitationSheet`): the rows as filtered now, and the acts. */
export type InvitationsContext = PlaceContext & {
  readonly items: readonly Invitation[];
  readonly loading: boolean;
  readonly actions: ReturnType<typeof useAccessActions>;
};

function readFilters(params: URLSearchParams): InvitationFilters {
  const status = params.get('status');
  return {
    q: params.get('q') ?? '',
    status: STATUSES.find((value) => value === status) ?? null,
    sort: params.get('sort') === 'expires' ? 'expires' : 'created',
  };
}

type InvitationList = ReturnType<typeof useListParams>;

function InvitationsToolbar({
  filters,
  list,
}: {
  readonly filters: InvitationFilters;
  readonly list: InvitationList;
}) {
  const { t } = useTranslation('administration');
  return (
    /* The shared filterable list (decision 101, #401): search, filters as labels, order. */
    <ListToolbar
      label={t('invitations.title')}
      search={{
        label: t('invitations.searchLabel'),
        value: filters.q,
        onWrite: (q) => list.set('q', q),
      }}
      fields={[
        {
          key: 'status',
          label: t('invitations.statusFilter'),
          options: STATUSES.map((status) => ({
            value: status,
            label: t(`invitations.statusOption.${status}`),
          })),
        },
      ]}
      active={list.active}
      onFilter={list.set}
      onClear={list.clear}
      sort={{
        label: t('invitations.sortLabel'),
        value: filters.sort,
        options: [
          { value: 'created', label: t('invitations.sortOption.created') },
          { value: 'expires', label: t('invitations.sortOption.expires') },
        ],
        onChange: (value) => list.set('sort', value === 'created' ? null : value),
      }}
    />
  );
}

/** A card that closed (Esc, ×, «Torna agli inviti») gives the focus back to its row, once. */
function useFocusClosedCard(sheetOpen: boolean, items: readonly Invitation[]) {
  const location = useLocation();
  const focusInvitation = (location.state as InvitationClosing | null)?.focusInvitation;
  const focusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (sheetOpen || focusInvitation === undefined || focusedFor.current === location.key) {
      return;
    }
    const row = Array.from(document.querySelectorAll<HTMLElement>('[data-invitation-link]')).find(
      (link) => link.dataset.invitationLink === focusInvitation,
    );
    if (row !== undefined) {
      row.focus();
      focusedFor.current = location.key;
    }
  }, [sheetOpen, focusInvitation, location.key, items]);
}

function InvitationsHead({
  titleId,
  placeName,
  canInvite,
  onInvite,
  filters,
  list,
}: {
  readonly titleId: string;
  readonly placeName: string;
  readonly canInvite: boolean;
  readonly onInvite: () => void;
  readonly filters: InvitationFilters;
  readonly list: InvitationList;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-head">
      <PageHead
        titleId={titleId}
        title={t('invitations.title')}
        summary={t('invitations.summary', { place: placeName })}
        actions={
          canInvite ? (
            <span className="page-primary">
              <Button onClick={() => onInvite()}>
                <Plus />
                {t('people.invite')}
              </Button>
            </span>
          ) : undefined
        }
      />
      <InvitationsToolbar filters={filters} list={list} />
    </div>
  );
}

function InvitationPanels({
  canInvite,
  inviting,
  setInviting,
  editingRoles,
  setEditingRoles,
  placeKind,
  placeId,
  placeName,
  reload,
}: {
  readonly canInvite: boolean;
  readonly inviting: boolean;
  readonly setInviting: (open: boolean) => void;
  readonly editingRoles: Invitation | null;
  readonly setEditingRoles: (invitation: Invitation | null) => void;
  readonly placeKind: 'organizations' | 'tenants';
  readonly placeId: string;
  readonly placeName: string;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <>
      {canInvite ? (
        <PhonePrimary>
          <Button onClick={() => setInviting(true)}>
            <Plus />
            {t('people.inviteShort')}
          </Button>
        </PhonePrimary>
      ) : null}
      <EditInvitationRolesPanel
        invitation={editingRoles}
        onOpenChange={(open) => {
          if (!open) setEditingRoles(null);
        }}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        onSaved={reload}
      />
      {canInvite ? (
        <InvitePanel
          open={inviting}
          onOpenChange={setInviting}
          placeKind={placeKind}
          placeId={placeId}
          placeName={placeName}
          onInvited={reload}
        />
      ) : null}
    </>
  );
}

/**
 * Inviti (FR-IN-4): every invitation of the place, pending, accepted, expired and withdrawn,
 * searchable by email and sortable by date. «Ritira» withdraws a pending one; «Invita di nuovo»
 * sends a new invitation with the same email and roles, on the authority of whoever presses it and
 * checked again — for a pending, expired or withdrawn one. An accepted invitation leads to the
 * person among the people. Each row offers only what its own `can` allows.
 */
export function InvitationsPage() {
  const { place, detail, refreshPlace } = usePlaceContext();
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind ?? 'organizations';
  const placeId = pair?.placeId ?? '';
  const placeName = detail.place.name;
  const list = useListParams(FILTER_KEYS);
  const filters = useMemo(() => readFilters(list.params), [list.params]);
  const { state, more, reload } = useInvitations(placeKind, placeId, filters);
  const actions = useAccessActions({
    placeKind,
    placeId,
    placeName,
    onChanged: reload,
    onStanding: refreshPlace,
  });
  const [inviting, setInviting] = useState(false);
  const [editingRoles, setEditingRoles] = useState<Invitation | null>(null);
  const titleId = useId();

  const filtered = filters.q !== '' || filters.status !== null;
  const listPath = sectionPath(place, 'invitations');
  const sheetOpen = useMatch(`${listPath}/:invitationId`) !== null;
  const childContext: InvitationsContext = {
    ...usePlaceContext(),
    items: state.items,
    loading: state.status === 'loading',
    actions,
  };
  const query = list.params.size > 0 ? `?${list.params.toString()}` : '';

  useFocusClosedCard(sheetOpen, state.items);

  return (
    <section
      aria-labelledby={titleId}
      className={cn('people-section flex flex-col gap-5', sheetOpen && 'people-section-with-sheet')}
    >
      <InvitationsHead
        titleId={titleId}
        placeName={placeName}
        canInvite={detail.can.invite}
        onInvite={() => setInviting(true)}
        filters={filters}
        list={list}
      />
      <div className={cn('people-layout', sheetOpen && 'people-layout-with-sheet')}>
        <div className="people-list-pane">
          <InvitationListPane
            state={state}
            more={more}
            reload={reload}
            filtered={filtered}
            canInvite={detail.can.invite}
            placeName={placeName}
            actions={actions}
            onClear={list.clear}
            onInvite={() => setInviting(true)}
            onEditRoles={setEditingRoles}
            peopleHrefFor={(invitation) =>
              `${sectionPath(place, 'people')}?q=${encodeURIComponent(invitation.email)}`
            }
            cardHrefFor={(invitation) => `${listPath}/${invitation.id}${query}`}
          />
        </div>
        <Outlet context={childContext} />
      </div>
      <InvitationPanels
        canInvite={detail.can.invite}
        inviting={inviting}
        setInviting={setInviting}
        editingRoles={editingRoles}
        setEditingRoles={setEditingRoles}
        placeKind={placeKind}
        placeId={placeId}
        placeName={placeName}
        reload={reload}
      />
    </section>
  );
}
